import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Define color themes
export const COLOR_THEMES = {
  ocean: {
    name: 'Ocean (Default)',
    light: {
      primaryColor: '#3b82f6',      // Blue
      secondaryColor: '#10b981',     // Green
      bgGradientStart: '#dbeafe',    // Light blue
      bgGradientEnd: '#bfdbfe',      // Medium blue
    },
    dark: {
      primaryColor: '#60a5fa',       // Lighter blue
      secondaryColor: '#34d399',     // Lighter green
      bgGradientStart: '#1e3a8a',    // Dark blue
      bgGradientEnd: '#1e40af',      // Darker blue
    }
  },
  sunset: {
    name: 'Sunset',
    light: {
      primaryColor: '#f59e0b',       // Amber/Orange
      secondaryColor: '#ec4899',     // Pink
      bgGradientStart: '#ffdfba',    // Peachy orange - more visible
      bgGradientEnd: '#ffc288',      // Warm orange
    },
    dark: {
      primaryColor: '#fbbf24',       // Lighter amber
      secondaryColor: '#f472b6',     // Lighter pink
      bgGradientStart: '#6b4e1f',    // Dark brown (from your image)
      bgGradientEnd: '#4a3315',      // Deeper brown
    }
  },
  forest: {
    name: 'Forest',
    light: {
      primaryColor: '#10b981',       // Green
      secondaryColor: '#14b8a6',     // Teal
      bgGradientStart: '#d4f1c5',    // Light mint green - more visible
      bgGradientEnd: '#b8e994',      // Medium mint green
    },
    dark: {
      primaryColor: '#34d399',       // Lighter green
      secondaryColor: '#5eead4',     // Lighter teal
      bgGradientStart: '#3d5a3d',    // Dark forest green (from your image)
      bgGradientEnd: '#2d4a2d',      // Deeper forest green
    }
  },
  lavender: {
    name: 'Lavender',
    light: {
      primaryColor: '#8b5cf6',       // Purple
      secondaryColor: '#a78bfa',     // Light purple
      bgGradientStart: '#e9dff9',    // Very light lavender - more visible
      bgGradientEnd: '#d9c9f7',      // Soft lavender
    },
    dark: {
      primaryColor: '#a78bfa',       // Lighter purple
      secondaryColor: '#c4b5fd',     // Even lighter purple
      bgGradientStart: '#4c1d95',    // Dark purple (from your image)
      bgGradientEnd: '#5b21b6',      // Medium dark purple
    }
  },
  rose: {
    name: 'Rose',
    light: {
      primaryColor: '#f43f5e',       // Rose
      secondaryColor: '#fb7185',     // Light rose
      bgGradientStart: '#ffe4ec',    // Very light pink - more visible
      bgGradientEnd: '#ffc9d9',      // Soft rose pink
    },
    dark: {
      primaryColor: '#fb7185',       // Lighter rose
      secondaryColor: '#fda4af',     // Even lighter rose
      bgGradientStart: '#9d174d',    // Dark rose/pink (from your image)
      bgGradientEnd: '#831843',      // Deep rose
    }
  }
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem('darkMode');
      return saved ? JSON.parse(saved) : false;
    } catch (error) {
      return false;
    }
  });

  const [colorTheme, setColorTheme] = useState(() => {
    return localStorage.getItem('colorTheme') || 'ocean';
  });

  const [dashboardMode, setDashboardMode] = useState(() => {
    return localStorage.getItem('dashboardMode') || 'simplified';
  });

  // Theme customizations loaded from backend
  const [themeCustomizations, setThemeCustomizations] = useState({});
  const [customizationsLoaded, setCustomizationsLoaded] = useState(false);
  
  // Preview mode state - initialize synchronously from URL and sessionStorage
  const [previewMode, setPreviewMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('preview') === 'true';
  });
  
  const [previewSettings, setPreviewSettings] = useState(() => {
    if (typeof window === 'undefined') return null;
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('preview') !== 'true') return null;
    
    try {
      const previewData = sessionStorage.getItem('theme_preview');
      if (previewData) {
        const parsed = JSON.parse(previewData);
        console.log('🎨 Preview settings initialized:', parsed);
        return parsed;
      }
    } catch (error) {
      console.error('Error parsing preview settings:', error);
    }
    return null;
  });

  // Handle initial theme/mode switch for preview (runs once)
  useEffect(() => {
    if (previewMode && previewSettings) {
      // Switch to the preview theme if different
      if (previewSettings.theme && previewSettings.theme !== colorTheme) {
        setColorTheme(previewSettings.theme);
        localStorage.setItem('colorTheme', previewSettings.theme); // Temporarily set for this session
      }
      
      // Switch to the preview mode (light/dark) if different
      if (previewSettings.mode === 'dark' && !isDarkMode) {
        setIsDarkMode(true);
      } else if (previewSettings.mode === 'light' && isDarkMode) {
        setIsDarkMode(false);
      }
      
      console.log('🎨 Preview mode theme/mode applied');
    }
  }, [previewMode]); // Only run when previewMode changes (essentially once)

  // Load theme customizations from backend
  useEffect(() => {
    const loadCustomizations = async () => {
      try {
        const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
        console.log('🎨 Loading theme customizations from:', apiUrl);
        
        const response = await fetch(`${apiUrl}/api/theme-customizations/all`);
        if (response.ok) {
          const data = await response.json();
          setThemeCustomizations(data.customizations || {});
          console.log('🎨 Theme customizations loaded:', Object.keys(data.customizations || {}));
        } else {
          console.error('🎨 Failed to load customizations:', response.status);
        }
      } catch (error) {
        console.error('Error loading theme customizations:', error);
      } finally {
        setCustomizationsLoaded(true);
      }
    };

    loadCustomizations();
  }, []);

  // Get the current theme's customization settings (with preview override)
  const getCurrentCustomization = () => {
    // Double-check: if URL has preview=true, always use preview settings
    const urlParams = new URLSearchParams(window.location.search);
    const isPreviewUrl = urlParams.get('preview') === 'true';
    
    // If in preview mode (either via state or URL), return the preview settings
    if ((previewMode || isPreviewUrl) && previewSettings) {
      console.log('🎨 getCurrentCustomization returning preview settings');
      return previewSettings.settings;
    }
    
    const themeCustom = themeCustomizations[colorTheme];
    if (!themeCustom) return null;
    
    const modeKey = isDarkMode ? 'dark' : 'light';
    return themeCustom[modeKey] || null;
  };

  // Apply theme colors to CSS variables
  useEffect(() => {
    const theme = COLOR_THEMES[colorTheme];
    if (!theme) return;
    
    const colors = isDarkMode ? theme.dark : theme.light;
    const root = document.documentElement;
    
    // Double-check URL for preview mode
    const urlParams = new URLSearchParams(window.location.search);
    const isPreviewUrl = urlParams.get('preview') === 'true';
    
    // Get customizations - use preview if available, else from backend
    let customization = null;
    if ((previewMode || isPreviewUrl) && previewSettings) {
      customization = previewSettings.settings;
      console.log('🎨 Applying preview customization:', customization);
    } else {
      const themeCustom = themeCustomizations[colorTheme];
      const modeKey = isDarkMode ? 'dark' : 'light';
      customization = themeCustom ? themeCustom[modeKey] : null;
    }
    
    // Set data attribute for CSS selector
    root.setAttribute('data-theme', colorTheme);
    root.setAttribute('data-mode', isDarkMode ? 'dark' : 'light');
    
    // Update CSS variables for colors
    root.style.setProperty('--primary-blue', colors.primaryColor);
    root.style.setProperty('--primary-green', colors.secondaryColor);
    root.style.setProperty('--secondary-blue', colors.primaryColor + 'dd');
    root.style.setProperty('--secondary-green', colors.secondaryColor + 'dd');
    root.style.setProperty('--accent-teal', colors.secondaryColor);
    
    // Determine background color (customized or default)
    const bgColor = customization?.background?.color || colors.bgGradientStart;
    
    // Update CSS variables for background gradients
    root.style.setProperty('--bg-page-start', bgColor);
    root.style.setProperty('--bg-page-end', customization?.background?.color ? bgColor : colors.bgGradientEnd);
    
    // Store customization data attributes
    // Set data-custom-bg if we have ANY custom background settings
    const hasCustomBg = customization?.background?.color || 
                        customization?.background?.image_id ||
                        customization?.background?.use_gradient;
    
    if (hasCustomBg) {
      root.setAttribute('data-custom-bg', 'true');
    } else {
      root.removeAttribute('data-custom-bg');
    }
    
    if (customization?.hub_cards) {
      root.setAttribute('data-custom-cards', 'true');
    } else {
      root.removeAttribute('data-custom-cards');
    }
    
    // Update wave pattern based on dark mode
    const bgPattern = isDarkMode ? 'url(./wave-light.png)' : 'url(./wave-dark.png)';
    root.style.setProperty('--bg-wave-pattern', bgPattern);
    
    console.log('🎨 THEME CHANGE:', colorTheme, isDarkMode ? 'dark' : 'light');
    console.log('🎨 Background:', bgColor);
    if (customization) {
      console.log('🎨 Custom settings applied:', customization);
    }
    
    // Check if we have ANY custom background settings
    const hasCustomBackground = customization?.background?.color || 
                                customization?.background?.image_id ||
                                customization?.background?.use_gradient;
    
    // Update containers - let CSS handle backgrounds via data attributes
    // Only need to set transparent for custom backgrounds
    const updateContainers = () => {
      const containers = document.querySelectorAll('.page-container, .min-h-screen');
      
      containers.forEach((container) => {
        if (hasCustomBackground) {
          // Custom background - let BackgroundWrapper handle it
          container.style.setProperty('background', 'transparent', 'important');
          container.style.setProperty('background-image', 'none', 'important');
        } else {
          // Default theme - CSS handles it via data-theme/data-mode attributes
          // Clear any inline styles so CSS can take over
          container.style.removeProperty('background');
          container.style.removeProperty('background-image');
          container.style.removeProperty('background-size');
          container.style.removeProperty('background-position');
          container.style.removeProperty('background-repeat');
          container.style.removeProperty('background-attachment');
          container.style.removeProperty('background-blend-mode');
        }
      });
    };
    
    // Update once after render
    updateContainers();
    
    // Use MutationObserver to catch dynamically added elements (no setTimeout hacks)
    const observer = new MutationObserver((mutations) => {
      // Only update if new page-container or min-h-screen elements were added
      const hasNewContainers = mutations.some(mutation => 
        Array.from(mutation.addedNodes).some(node => 
          node.nodeType === 1 && (
            node.classList?.contains('page-container') || 
            node.classList?.contains('min-h-screen') ||
            node.querySelector?.('.page-container, .min-h-screen')
          )
        )
      );
      if (hasNewContainers) {
        updateContainers();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, [colorTheme, isDarkMode, themeCustomizations, customizationsLoaded, previewMode, previewSettings]);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => setIsDarkMode(!isDarkMode);

  const changeColorTheme = (themeName) => {
    if (COLOR_THEMES[themeName]) {
      setColorTheme(themeName);
      localStorage.setItem('colorTheme', themeName);
    }
  };

  const changeDashboardMode = (mode) => {
    if (mode === 'simplified' || mode === 'freeplay') {
      setDashboardMode(mode);
      localStorage.setItem('dashboardMode', mode);
    }
  };

  // Load user preferences from backend (called when user logs in)
  const loadUserPreferences = useCallback(async (token) => {
    if (!token) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/preferences`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const prefs = data.preferences;
        
        if (prefs) {
          // Apply saved preferences
          if (prefs.colorTheme && COLOR_THEMES[prefs.colorTheme]) {
            setColorTheme(prefs.colorTheme);
            localStorage.setItem('colorTheme', prefs.colorTheme);
          }
          if (typeof prefs.isDarkMode === 'boolean') {
            setIsDarkMode(prefs.isDarkMode);
            localStorage.setItem('darkMode', JSON.stringify(prefs.isDarkMode));
          }
          console.log('🎨 User preferences loaded from server:', prefs);
        }
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  }, []);

  // Save user preferences to backend
  const saveUserPreferences = useCallback(async (token) => {
    if (!token) return;
    
    try {
      const preferences = {
        colorTheme,
        isDarkMode
      };
      
      await fetch(`${BACKEND_URL}/api/user/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });
      
      console.log('🎨 User preferences saved to server');
    } catch (error) {
      console.error('Error saving user preferences:', error);
    }
  }, [colorTheme, isDarkMode]);

  // Reload customizations (called after admin makes changes)
  const reloadCustomizations = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/theme-customizations/all`);
      if (response.ok) {
        const data = await response.json();
        setThemeCustomizations(data.customizations || {});
      }
    } catch (error) {
      console.error('Error reloading theme customizations:', error);
    }
  };

  const value = {
    isDarkMode,
    setIsDarkMode,
    toggleDarkMode,
    colorTheme,
    changeColorTheme,
    dashboardMode,
    changeDashboardMode,
    themeCustomizations,
    getCurrentCustomization,
    reloadCustomizations,
    customizationsLoaded,
    previewMode,
    previewSettings,
    loadUserPreferences,
    saveUserPreferences,
    availableThemes: Object.keys(COLOR_THEMES).map(key => ({
      key,
      name: COLOR_THEMES[key].name,
      colors: COLOR_THEMES[key]
    }))
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
